'use client';

import { useEffect, useRef } from 'react';

/**
 * The dark "Built for Real Businesses" section, whose only interactive part is a glow that
 * follows the cursor. That glow is the sole reason this markup needs JavaScript, so it lives
 * here on its own — the section's actual content arrives as `children` from the server
 * component, which keeps that markup out of the client bundle entirely.
 *
 * Pure CSS + a mousemove listener, no animation library: the smoothing/easing "lag" that GSAP's
 * quickTo used to provide comes from the `transition` on the follower div below — setting a new
 * `transform`/`opacity` on it just lets that CSS transition animate to the new value.
 */
const HeroGlow = ({ children }: { children: React.ReactNode }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const $follower = useRef<HTMLDivElement>(null);

  // The cursor-follow glow is a mouse-only flourish, so skip touch devices entirely.
  useEffect(() => {
    const section = sectionRef.current;
    const follower = $follower.current;
    if (!section || !follower || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      const isInside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      if (isInside) {
        const x = e.clientX - rect.left - follower.offsetWidth / 2;
        const y = e.clientY - rect.top - follower.offsetHeight / 2;
        // translate(-50%, -50%) keeps the glow centered on its own coordinate; the second
        // translate() is the actual cursor offset — CSS composes chained translates additively.
        follower.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
        follower.style.opacity = '1';
      } else {
        follower.style.opacity = '0';
      }
    };
    const handleEnter = () => { follower.style.opacity = '1'; };
    const handleLeave = () => { follower.style.opacity = '0'; };

    section.addEventListener('mousemove', handleMouseMove);
    section.addEventListener('mouseenter', handleEnter);
    section.addEventListener('mouseleave', handleLeave);

    return () => {
      section.removeEventListener('mousemove', handleMouseMove);
      section.removeEventListener('mouseenter', handleEnter);
      section.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="backdrop-blur-3xl !overflow-hidden bg-[#010839] relative group"
    >
      <div
        ref={$follower}
        className="pointer-events-none absolute aspect-square w-[30vmin] rounded-full will-change-transform opacity-0 transition-[transform,opacity] duration-200 ease-out"
        style={{ transform: 'translate(-50%, -50%)' }}>
        <div className="w-[254px] h-[254px] rounded-[454px] bg-[#0f1192] mix-blend-plus-lighter blur-[100px] animate-spin-slow ">
        </div>
      </div>

      {children}
    </section>
  );
};

export default HeroGlow;
